// src/pages/Gratuit.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, where, doc, getDoc, getDocs } from "firebase/firestore";
import { getStorage, getDownloadURL, ref as sRef } from "firebase/storage";
import { useSearchParams } from "react-router-dom";
// Arrière-plan importé comme pour Prof (profBg)
import gratuitBg from "../assets/1162777_ORHG040.jpg";
import PreviewPanel from "../components/PreviewPanel";
import { titleOf, extOf, r2UrlFromKey } from "../utils/fileUtils";

// Matières canoniques et synonymes
const SUBJECTS_3ACFR = [
  { code: "FR", label: "Français", synonyms: ["fr", "fran", "français", "francais"] },
  { code: "HG", label: "Histoire-Géo", synonyms: ["hg", "histoire", "géographie", "geographie"] },
  { code: "EI", label: "Éducation Islamique", synonyms: ["ei", "islam", "islamique"] },
  { code: "MATH", label: "Mathématiques", synonyms: ["math", "maths", "mathématiques", "mathematiques"] },
  { code: "PHYS", label: "Physique", synonyms: ["phys", "physique"] },
  { code: "SVT", label: "SVT", synonyms: ["svt"] },
  { code: "AR", label: "Arabe", synonyms: ["ar", "arabe", "arab", "arabi", "arabic"] },
];

const SUBJECTS_2BAC = [
  { code: "PHILO", label: "Philo", synonyms: ["philo", "philosophie", "philosophy"] },
  { code: "MATH", label: "Mathématiques", synonyms: ["math", "maths", "mathématiques", "mathematiques"] },
  { code: "PHYS", label: "Physique", synonyms: ["phys", "physique"] },
  { code: "SVT", label: "SVT", synonyms: ["svt"] },
  { code: "ANGL", label: "Anglais", synonyms: ["ang", "anglais", "english"] },
];

export default function Gratuit() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const storage = getStorage();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [crumbs, setCrumbs] = useState([{ id: null, name: "Racine" }]);
  const [ownerSubjects, setOwnerSubjects] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") || "3acfr").toLowerCase();
  const [activeTab, setActiveTab] = useState(initialTab === "2bac" ? "2bac" : "3acfr");
  const initialSubRaw = (searchParams.get("sub") || "").toUpperCase();
  const subjectsForInit = (activeTab === "2bac" ? SUBJECTS_2BAC : SUBJECTS_3ACFR);
  const defaultSubject = subjectsForInit[0].code;
  const [selectedSubject, setSelectedSubject] = useState(
    subjectsForInit.some((s) => s.code === initialSubRaw) ? initialSubRaw : defaultSubject
  );

  useEffect(() => {
    setLoading(true);
    setError("");

    const col = collection(db, "contents");
    // Requêtes candidates pour marquer le contenu gratuit/public
    const qFree      = query(col, where("free", "==", true));
    const qGratuit   = query(col, where("scope", "==", "gratuit"));
    const qPublic    = query(col, where("scope", "==", "public"));
    const qFreeScope = query(col, where("scope", "==", "free"));

    let sFree = [], sGratuit = [], sPublic = [], sFreeScope = [];

    const fetchOnce = async () => {
      try {
        const [dFree, dGratuit, dPublic, dFreeScope] = await Promise.all([
          getDocs(qFree),
          getDocs(qGratuit),
          getDocs(qPublic),
          getDocs(qFreeScope),
        ]);
        sFree = dFree.docs; sGratuit = dGratuit.docs; sPublic = dPublic.docs; sFreeScope = dFreeScope.docs;
        mergeAndSet();
      } catch (e) {
        console.error("fetchOnce gratuit failed", e);
        setError("Impossible de charger le contenu gratuit.");
        setLoading(false);
      }
    };

    const mergeAndSet = () => {
      const map = new Map();
      for (const d of [...sFree, ...sGratuit, ...sPublic, ...sFreeScope]) {
        const c = d.data();
        if (c.published === false) continue;
        map.set(d.id, { id: d.id, ...c });
      }
      const arr = Array.from(map.values()).sort((a, b) => {
        const ta = a?.createdAt?.seconds || 0;
        const tb = b?.createdAt?.seconds || 0;
        return tb - ta;
      });
      setItems(arr);
      // Charger la matière des propriétaires (fallback si un contenu n’a pas de sujet)
      const uids = Array.from(new Set(arr.map((c) => c.ownerUid).filter(Boolean)));
      if (uids.length) {
        Promise.all(
          uids.map(async (uid) => {
            try {
              const snap = await getDoc(doc(db, "users", uid));
              const data = snap.exists() ? snap.data() : null;
              return { uid, subject: data?.subject || data?.matiere || null };
            } catch {
              return { uid, subject: null };
            }
          })
        ).then((rows) => {
          const mapSubj = {};
          rows.forEach(({ uid, subject }) => {
            if (subject) mapSubj[uid] = subject;
          });
          setOwnerSubjects((prev) => ({ ...prev, ...mapSubj }));
        });
      }
      setLoading(false);
    };

    const unsubs = [];
  unsubs.push(onSnapshot(qFree,      s => { sFree      = s.docs; mergeAndSet(); }, e => { console.error(e); fetchOnce(); }));
  unsubs.push(onSnapshot(qGratuit,   s => { sGratuit   = s.docs; mergeAndSet(); }, e => { console.error(e); fetchOnce(); }));
  unsubs.push(onSnapshot(qPublic,    s => { sPublic    = s.docs; mergeAndSet(); }, e => { console.error(e); fetchOnce(); }));
  unsubs.push(onSnapshot(qFreeScope, s => { sFreeScope = s.docs; mergeAndSet(); }, e => { console.error(e); fetchOnce(); }));

    // Fallback sur timeout si aucune donnée reçue au bout de 2s
    const t = setTimeout(() => {
      if (!sFree.length && !sGratuit.length && !sPublic.length && !sFreeScope.length) {
        fetchOnce();
      }
    }, 2000);

    return () => { unsubs.forEach(u => u && u()); clearTimeout(t); };
  }, []);

  // Sync URL param when tab changes
  useEffect(() => {
    const current = (searchParams.get("tab") || "").toLowerCase();
    const next = activeTab;
    if (current !== next) {
      searchParams.set("tab", next);
      setSearchParams(searchParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Ajuster la matière sélectionnée si l'onglet change
  useEffect(() => {
    const subs = activeTab === "2bac" ? SUBJECTS_2BAC : SUBJECTS_3ACFR;
    const inList = subs.some((s) => s.code === selectedSubject);
    if (!inList) setSelectedSubject(subs[0].code);
  }, [activeTab]);

  // Sync URL param pour la matière
  useEffect(() => {
    const currentSub = (searchParams.get("sub") || "").toUpperCase();
    const nextSub = selectedSubject.toUpperCase();
    if (currentSub !== nextSub) {
      searchParams.set("sub", nextSub);
      setSearchParams(searchParams, { replace: true });
    }
  }, [selectedSubject, searchParams, setSearchParams]);

  const urlOf = async (c) => {
    if (c?.r2Key) return r2UrlFromKey(c.r2Key);
    const p = c.storagePath || c.filePath;
    if (!p) return null;
    try {
      const u = await getDownloadURL(sRef(storage, p));
      return u;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const openPreview = async (c) => {
    const url = await urlOf(c);
    if (!url) return;
    setPreview({ doc: c, url });
    // Scroll sur le lecteur
    setTimeout(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };



  const resetPreview = () => setPreview(null);

  // Empêcher clic droit et raccourcis de téléchargement (scopé à la page)
  const preventDangerousKeys = (e) => {
    const k = e.key.toLowerCase();
    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    // Bloquer quelques combinaisons communes: Ctrl+S, Ctrl+P, Ctrl+U, F12, Ctrl+Shift+I/J/C
    if (
      k === "f12" ||
      (ctrl && (k === "s" || k === "p" || k === "u")) ||
      (ctrl && shift && (k === "i" || k === "j" || k === "c"))
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Helpers de classification
  const normalize = (v) => (v || "").toString().toLowerCase();
  const matchesSynonym = (text, synonym) => {
    const s = (synonym || "").toLowerCase();
    if (s.length < 3) return false; // éviter faux positifs (ex: "fr" dans "free", "ar" dans "folder")
    const t = (text || "").toLowerCase();
    try {
      const re = new RegExp(`(^|[^a-z0-9])${s}([^a-z0-9]|$)`, "i");
      return re.test(t);
    } catch {
      return t.includes(s);
    }
  };
  const is3ACFr = (c) => {
    const scope = normalize(c.scope);
    const section = normalize(c.section || c.level);
    const folderPath = normalize(c.folderPath || c.path);
    const is3ac = scope.includes("3ac") || section.includes("3ac") || folderPath.includes("3ac");
    return is3ac;
  };
  const is2Bac = (c) => {
    const scope = normalize(c.scope);
    const section = normalize(c.section || c.level);
    const folderPath = normalize(c.folderPath || c.path);
    return scope.includes("2bac") || section.includes("2bac") || folderPath.includes("2bac");
  };

  const subjectMatches = (c, subjectCode) => {
    const list = activeTab === "2bac" ? SUBJECTS_2BAC : SUBJECTS_3ACFR;
    const def = list.find((s) => s.code === subjectCode);
    if (!def) return true;

    const subj = normalize(c.freeSubject || c.subject || ownerSubjects[c.ownerUid] || c.matiere);
    if (subj) {
      if (subj === def.code.toLowerCase()) return true;
      const sy = def.synonyms || [];
      if (sy.some((k) => k.length >= 3 && subj.includes(k.toLowerCase()))) return true;
      return false;
    }

    const title = normalize(c.title || c.name || "");
    const folderPath = normalize(c.folderPath || c.path || "");
    const sy = def.synonyms || [];
    if (sy.some((k) => matchesSynonym(title, k) || matchesSynonym(folderPath, k))) return true;
    return false;
  };

  // Charger les dossiers (free) sous le dossier courant
  useEffect(() => {
    const col = collection(db, "folders");
    const q = query(col, where("free", "==", true), where("parentId", "==", currentFolderId || null));
    const unsub = onSnapshot(q, (snap) => {
      let arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      // Filtrer par section: 3AC Fr vs 2Bac, garder legacy sans tag
      arr = arr.filter((f) => {
        const is3 = is3ACFr(f);
        const is2 = is2Bac(f);
        if (activeTab === "2bac") return is2 || (!is3 && !is2);
        return is3 || (!is3 && !is2);
      });
      // Filtrer par matière sélectionnée (comme pour les contenus)
      arr = arr.filter((f) => subjectMatches(f, selectedSubject));
      arr.sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));
      setFolders(arr);
    }, (e) => console.error(e));
    return () => unsub();
  }, [activeTab, currentFolderId, selectedSubject]);

  // Fil d'ariane gratuit
  useEffect(() => {
    (async () => {
      if (!currentFolderId) { setCrumbs([{ id: null, name: "Racine" }]); return; }
      const trail = [];
      let nodeId = currentFolderId;
      for (let i = 0; i < 20 && nodeId; i++) {
        const s = await getDoc(doc(db, "folders", nodeId));
        if (!s.exists()) break;
        const d = s.data();
        trail.push({ id: s.id, name: d.name, parentId: d.parentId || null });
        nodeId = d.parentId || null;
      }
      trail.reverse();
      setCrumbs([{ id: null, name: "Racine" }, ...trail]);
    })();
  }, [currentFolderId]);

  const items3acFr = useMemo(() => items.filter((c) => is3ACFr(c)), [items]);
  const items2bac = useMemo(() => items.filter((c) => is2Bac(c)), [items]);
  const baseItems = activeTab === "2bac" ? items2bac : items3acFr;
  const baseSubjectItems = baseItems.filter((c) => subjectMatches(c, selectedSubject));
  const visibleItems = baseSubjectItems.filter((c) => (currentFolderId ? c.folderId === currentFolderId : (c.folderId || null) === null));
  const subjects = activeTab === "2bac" ? SUBJECTS_2BAC : SUBJECTS_3ACFR;

  return (
    <>
      {/* Arrière-plan global (n’affecte pas le contenu) */}
      <div
        className="gratuit-bg"
        aria-hidden="true"
        style={{ "--gratuit-bg": `url(${gratuitBg})` }}
      />

      {/* Conteneur de page pour scoper les styles transparents */}
      <div
        className="gratuit-page"
        tabIndex={0}
        onKeyDownCapture={preventDangerousKeys}
      >
        {/* Section héro supprimée selon demande */}
        <div className="container py-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h1 className="h5 mb-0">Contenu gratuit — {activeTab === "2bac" ? "2Bac" : "3AC Fr"}</h1>
            <span className="badge text-bg-success">Ouvert à tous</span>
          </div>

        {/* Fil d'Ariane */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            {crumbs.map((c, idx) => (
              <li key={c.id || `root-${idx}`} className={`breadcrumb-item ${idx === crumbs.length - 1 ? "active" : ""}`} aria-current={idx === crumbs.length - 1 ? "page" : undefined}>
                {idx === crumbs.length - 1 ? (
                  c.name
                ) : (
                  <button className="btn btn-link p-0" onClick={() => setCurrentFolderId(c.id)}>{c.name}</button>
                )}
              </li>
            ))}
          </ol>
        </nav>

        {/* Sous-onglets (Section) */}
        <ul className="nav nav-pills mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "3acfr" ? "active" : ""}`}
              onClick={() => setActiveTab("3acfr")}
            >
              3AC Fr
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "2bac" ? "active" : ""}`}
              onClick={() => setActiveTab("2bac")}
            >
              2Bac
            </button>
          </li>
        </ul>

        {/* Sous-onglets (Matières) */}
        <ul className="nav nav-tabs mb-3">
          {subjects.map((s) => (
            <li key={s.code} className="nav-item">
              <button
                className={`nav-link ${selectedSubject === s.code ? "active" : ""}`}
                onClick={() => setSelectedSubject(s.code)}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>

        {error && <div className="alert alert-warning">{error}</div>}
        {loading && <div className="text-muted">Chargement…</div>}

        {!loading && visibleItems.length === 0 && (
          <div className="alert alert-info">Aucun contenu gratuit pour ce sous-onglet pour le moment.</div>
        )}

        {/* Dossiers */}
        {folders.length > 0 && (
          <>
            <h6 className="text-muted mb-2">Dossiers</h6>
            <div className="row g-3 mb-3">
              {folders.map((f) => (
                <div key={f.id} className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title" style={{ fontSize: "1rem" }}>{f.name || "Sans nom"}</h5>
                      <div className="mt-auto">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => setCurrentFolderId(f.id)}>Ouvrir</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Liste des contenus */}
        <div className="row g-3">
          {visibleItems.map((c) => (
            <div key={c.id} className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title" style={{ fontSize: "1rem" }}>{titleOf(c)}</h5>
                  {c.description && <p className="card-text text-muted" style={{ fontSize: "0.9rem" }}>{c.description}</p>}
                  <div className="mt-auto d-flex gap-2">
                    <button className="btn btn-sm btn-primary" onClick={() => openPreview(c)}>Lire</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lecteur basique */}
        <div className="mt-4" ref={previewRef}>
          {preview ? (
            <>
              <PreviewPanel 
                preview={preview} 
                isFullscreen={isFullscreen} 
              />
              <div className="mt-2 d-flex gap-2">
                <button className="btn btn-sm btn-light" onClick={resetPreview}>Fermer</button>
              </div>
            </>
          ) : (
            <div className="text-muted text-center p-5 border rounded bg-white">
              Sélectionnez un élément pour le prévisualiser ici.
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Styles locaux pour l’arrière-plan global */}
      <style>{`
        .gratuit-bg{
          position: absolute;
          inset: 0;
          z-index: -1;
          background-image: var(--gratuit-bg);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        /* Désactiver la sélection et le drag pour limiter les copies */
        .gratuit-page { user-select: none; -webkit-user-select: none; }
        /* Rendre le contenu translucide pour laisser apparaître l'arrière-plan */
        .gratuit-page .card,
        .gratuit-page .card .card-body,
        .gratuit-page .nav-tabs .nav-link,
        .gratuit-page .nav-pills .nav-link,
        .gratuit-page .breadcrumb,
        .gratuit-page .alert {
          background-color: rgba(255, 255, 255, 0.65) !important;
          backdrop-filter: saturate(120%) blur(3px);
        }
        .gratuit-page .card {
          border-color: rgba(255, 255, 255, 0.35);
        }
        /* Couleur du contenu en noir pour meilleure lisibilité */
        .gratuit-page,
        .gratuit-page h1,
        .gratuit-page h2,
        .gratuit-page h3,
        .gratuit-page h4,
        .gratuit-page h5,
        .gratuit-page h6,
        .gratuit-page p,
        .gratuit-page .card-title,
        .gratuit-page .card-text,
        .gratuit-page .breadcrumb,
        .gratuit-page .nav-link,
        .gratuit-page .alert {
          color: #000 !important;
        }
      `}</style>
    </>
  );
}